import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChallengeBattleShipService, DefaultConfBattleBoat, PlacedShip, ShotPlaceholder, TOrientation, TSHIPS_ID } from 'src/app/gamification/command-center/services/challenge-battle-ship.service';

@Component({
  selector: 'cv-board',
  templateUrl: './board.component.html',
  styleUrls: ['./board.component.scss']
})
export class BoardComponent implements OnInit, OnDestroy {

  @Input() canShot = false;
  @Input() resetBoard: boolean = false;


  @Output() saveBattleBoat = new EventEmitter<void>();
  @Output() goBack = new EventEmitter<void>();

  placedShips: PlacedShip[] = [];
  sunkenShips: PlacedShip[] = [];
  player: { GameStatus: number } = null;
  board = [];
  ships = [];
  shots: ShotPlaceholder[][] = [];
  selectedShip: { id: string, size: number, orientation: TOrientation } | null = null;
  hoverRow: number | null = null;
  hoverCol: number | null = null;
  cellSize: number = DefaultConfBattleBoat.ShootCellSize;
  private subscriptions = new Subscription();

  constructor(private challengeBattleBoatService: ChallengeBattleShipService) {
    this.challengeBattleBoatService.setInitialData();
  }

  ngOnInit(): void {
    this.subscriptions.add(this.challengeBattleBoatService.placedShips$.subscribe(placedShips => {
      this.placedShips = placedShips;
    }));
    this.subscriptions.add(this.challengeBattleBoatService.board$.subscribe(board => {
      this.board = board;
    }));
    this.subscriptions.add(this.challengeBattleBoatService.ships$.subscribe(ships => {
      this.ships = ships;
    }));
    this.subscriptions.add(this.challengeBattleBoatService.shots$.subscribe(shots => {
      this.shots = shots;
    }));

    this.subscriptions.add(this.challengeBattleBoatService.sunkenShips$.subscribe(sunkenShips => {
      this.sunkenShips = sunkenShips;
    }));
    this.subscriptions.add(this.challengeBattleBoatService.player$.subscribe(player => {
      this.player = player;
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.challengeBattleBoatService.setInitialData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.resetBoard) {
      this.resetBoardState();
    }
  }

  resetBoardState(): void {
    this.selectedShip = null;
    this.hoverRow = null;
    this.hoverCol = null;
  }

  getShipImageUrl(ship: { id: string, size: number, orientation: string }) {
    return `../App_Themes/clearview-default/images/challenges/battleboat/${ship.id.toLowerCase()}.svg`;
  }

  onCellClick(row: number, col: number): void {
    if (this.shots[row] && this.shots[row][col] && this.shots[row][col].shot) return;

    if (!this.selectedShip) {
      return;
    }

    // Checks if the boat can be placed in the selected position
    if (this.canPlaceShipAt(row, col)) {
      this.placeShipAt(row, col);
    }
  }

  canPlaceShipAt(row: number, col: number): boolean {
    if (!this.selectedShip) return false;

    let canPlace = true;
    for (let i = 0; i < this.selectedShip.size; i++) {
      let checkRow = row, checkCol = col;
      if (this.selectedShip.orientation === 'horizontal') checkCol += i;
      else checkRow += i;

      // Check if the ship goes off the board or overlaps with another
      if (checkRow >= this.board.length || checkCol >= this.board[0].length || this.board[checkRow][checkCol] !== '') {
        canPlace = false;
        break;
      }
    }
    return canPlace;
  }

  placeShipAt(row: number, col: number): void {
    if (!this.selectedShip) return;

    // The size of each cell is 23px and 3px was added for the space that the table has on the left
    const { positionX, positionY } = this.challengeBattleBoatService.getCellPositions(col, row, this.cellSize, 0);

    // Add the ship to the list of placed ships with its position
    this.challengeBattleBoatService.addShip({
      id: this.selectedShip.id,
      size: this.selectedShip.size,
      orientation: this.selectedShip.orientation,
      position: { x: positionX, y: positionY }
    });

    // Board and ship status update
    for (let i = 0; i < this.selectedShip.size; i++) {
      if (this.selectedShip.orientation === 'horizontal') {
        this.challengeBattleBoatService.markCell(row, col + i, this.selectedShip.id); // Mark the cell horizontally
      } else {
        this.challengeBattleBoatService.markCell(row + i, col, this.selectedShip.id); // Mark the cell vertically
      }
    }
    // Mark the ship as placed
    this.challengeBattleBoatService.setPlaceShip(this.selectedShip.id);

    // Deselect the ship after placing it
    this.selectedShip = null;
  }

  rotateShip(shipId: TSHIPS_ID): void {
    this.challengeBattleBoatService.rotateShip(shipId);
  }

  rotateSelectedShip(): void {
    if (this.selectedShip) {
      this.selectedShip.orientation = this.selectedShip.orientation === 'horizontal' ? 'vertical' : 'horizontal';
    }
  }

  selectShip(shipId: string): void {
    if (this.selectedShip && this.selectedShip.id === shipId) {
      // If the ship is already selected, deselect it
      this.selectedShip = null;
    } else {
      // Select the new ship
      this.selectedShip = this.ships.find(ship => ship.id === shipId) || null;
    }
  }

  onCellMouseEnter(row: number, col: number): void {
    if (this.selectedShip) {
      this.hoverRow = row;
      this.hoverCol = col;
    }
  }

  onCellMouseLeave(): void {
    this.hoverRow = null;
    this.hoverCol = null;
  }

  // This function checks if the space is occupied for ship placement.
  isSpaceOccupied(row: number, col: number): boolean {
    if (!this.selectedShip) return false;

    // Check each cell the ship would occupy
    for (let i = 0; i < this.selectedShip.size; i++) {
      const checkRow = this.selectedShip.orientation === 'vertical' ? row + i : row;
      const checkCol = this.selectedShip.orientation === 'horizontal' ? col + i : col;

      if (checkRow >= this.board.length || checkCol >= this.board[0].length || this.board[checkRow][checkCol] !== '') {
        return true;
      }
    }

    return false;
  }

  // This function returns an object with highlight and invalid flags
  // Determines whether a cell should be highlighted and if the highlight is invalid.
  shouldHighlight(row: number, col: number): { highlight: boolean, invalid: boolean } {
    if (!this.selectedShip || this.hoverRow === null || this.hoverCol === null) {
      return { highlight: false, invalid: false };
    }

    const shipSize = this.selectedShip.size;
    const shipOrientation = this.selectedShip.orientation;
    const boardSize = this.board.length;

    let highlight = false;
    let invalid = false;

    // Check if the ship would go off the board or overlap with another ship
    if (shipOrientation === 'horizontal') {
      const shipEndCol = this.hoverCol + shipSize - 1;
      highlight = row === this.hoverRow && col >= this.hoverCol && col <= shipEndCol;
      invalid = shipEndCol >= boardSize || this.isSpaceOccupied(this.hoverRow, this.hoverCol);
    } else {
      const shipEndRow = this.hoverRow + shipSize - 1;
      highlight = col === this.hoverCol && row >= this.hoverRow && row <= shipEndRow;
      invalid = shipEndRow >= boardSize || this.isSpaceOccupied(this.hoverRow, this.hoverCol);
    }

    return { highlight, invalid };
  }

  getPlacedShipStyle(ship: PlacedShip): Object {
    const size = ship.size >= DefaultConfBattleBoat.ShootCellSize ? ship.size : ship.size * DefaultConfBattleBoat.ShootCellSize;

    return {
      'left.px': ship.position.x + (ship.orientation === 'vertical' ? 7 : 0),
      'top.px': ship.position.y,
      'width.px': size,
      'height.px': DefaultConfBattleBoat.ShootCellSize - 1,
      'transform': ship.orientation === 'vertical' ? 'rotate(90deg) translate(0px, -20px)' : '',
      'transform-origin': ship.orientation === 'vertical' ? '0 0' : ""
    };
  }

  saveBattleBoatFn = () => {
    if (this.placedShips.length === this.ships.length && this.ships.length > 0)
      this.saveBattleBoat.emit();
  }
  
  goBackFn = () => {
    this.challengeBattleBoatService.setInitialData();
    this.goBack.emit();
  }
}